import { Notification } from "./components/centerOperationsCards/EquipmentsStatus";
import type { InverterStatusViewModel } from "./helpers/mapInverterOperationalStatus";

export type CenterOperationsData = {
  kpiData: {
    potenciaAtual: number;
    desvioDeMeta: number;
    energiaGerada: number;
    performance: number;
    disponibilidade: number;
    IGU: number;
  };
  iguData: {
    title: string;
    description: string;
    value: number;
    subValue: number;
  }[];
  topCausesData: {
    title: string;
    points: number;
  }[];
  performanceData: {
    name: string;
    status: "online" | "offline" | "maintenance";
    sector: string;
    message?: string | null;
    performanceRatio: number | null;
    potencia: number;
    eficiencia: number | null;
    irradiancia: number;
    irradianciaMaxima: number;
    temperatura: number;
    activeSensorsPercentage: number | null;
  }[];
  recentEventsData: {
    usinaCode: string;
    text: string;
    hour: string;
    status: "danger" | "warning";
  }[];
  suggestionEventsData: {
    severity: "alta" | "media" | "baixa";
    text: string;
  }[];
  ativosDaUsina: {
    planta: string;
    alarmUsinaId: number;
    alarmCode: string;
    alarmUg: string;
    alarmArea: string;
    alarmString: string;
    alarmMessage: string;
    alarmLastActiveDate: string;
    nomeDoAtivo: string;
    status: string;
    ultimoAlarme: string;
    tempoParado: string;
    isActive: boolean;
    description: string;
    usinaPhoneNumber: string;
  }[];
  iguScoreData: { value: number; last7days: number; last30days: number };
  equipamentosInfoData: {
    title: string;
    online: number;
    offline: number;
    maintenance: number;
    performance: number;
    available?: number;
    totalUnits: number;
    percent: number;
    updatedAt: string;
    notifications: Notification[];
    operationalStatuses?: InverterStatusViewModel[];
    hidePerformance?: boolean;
    isTrafo?: boolean;
  }[];

  isRopCard3001Loaded: boolean;
  isRopCard3002Loaded: boolean;
};

export type MonthlyMetric = {
  january: number;
  february: number;
  march: number;
  april: number;
  may: number;
  june: number;
  july: number;
  august: number;
  september: number;
  october: number;
  november: number;
  december: number;
};

export type PowerPlantAvailabilityAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  average: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
  powerPlantsCount: number;
};

export type ClusterAvailabilityAverage = {
  clusterId?: string;
  clusterName: string;
  average: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
  powerPlantsCount: number;
};

export type RopCard1101DisponibilidadeMediaTaticaoAggregated = {
  type: 1101;
  title: string;
  overallAverage: number;
  overallMonthlyValues: MonthlyMetric;
  overallYtdValue: number;
  powerPlantAverages: PowerPlantAvailabilityAverage[];
  clusterAverages: ClusterAvailabilityAverage[];
};

export type PowerPlantSpecificProductivityAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  average: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
  totalGeneratedEnergy: number;
  peakInstalledPower: number;
  powerPlantsCount: number;
};

export type ClusterSpecificProductivityAverage = {
  clusterId?: string;
  clusterName: string;
  average: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
  powerPlantsCount: number;
};

export type RopCard1102ProdutividadeEspecificaTaticaoAggregated = {
  type: 1102;
  title: string;
  overallAverage: number;
  overallMonthlyValues: MonthlyMetric;
  overallYtdValue: number;
  powerPlantAverages: PowerPlantSpecificProductivityAverage[];
  clusterAverages: ClusterSpecificProductivityAverage[];
};

export type TargetImpact = {
  target: number;
  generatedEnergy: number;
  deviationMWh: number;
  deviationPercent: number;
  targetMes: number;
  generatedEnergyMes: number;
};

export type ImpactoMetaPowerPlantAggregated = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodData: TargetImpact;
  monthlyData: Record<string, TargetImpact>;
  ytdData: TargetImpact;
};

export type ImpactoMetaClusterAggregated = {
  clusterId?: string;
  clusterName: string;
  periodData: TargetImpact;
  monthlyData: Record<string, TargetImpact>;
  ytdData: TargetImpact;
  powerPlantData: ImpactoMetaPowerPlantAggregated[];
};

export type RopCard1103ImpactoNaMetaTaticoAggregated = {
  type: 1103;
  title: string;
  periodData: TargetImpact;
  monthlyData: Record<string, TargetImpact>;
  ytdData: TargetImpact;
  clusterData: ImpactoMetaClusterAggregated[];
  powerPlantData: ImpactoMetaPowerPlantAggregated[];
};

export type PerformanceRatioPowerPlantAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodValue: number;
  expectedPerformanceRatio: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type PerformanceRatioClusterAverage = {
  clusterId?: string;
  clusterName: string;
  periodValue: number;
  expectedPerformanceRatio: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type RopCard1104PerformanceRatioTaticoAggregated = {
  type: 1104;
  title: string;
  overallAverage: number;
  expectedPerformanceRatio: number;
  overallMonthlyValues: MonthlyMetric;
  overallYtdValue: number;
  powerPlantAverages: PerformanceRatioPowerPlantAverage[];
  clusterAverages: PerformanceRatioClusterAverage[];
};

export type EnergyPerformanceIndexPowerPlantAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodValue: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type EnergyPerformanceIndexClusterAverage = {
  clusterId?: string;
  clusterName: string;
  periodValue: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type RopCard1105EnergyPerformanceIndexTaticoAggregated = {
  type: 1105;
  title: string;
  overallAverage: number;
  overallMonthlyValues: MonthlyMetric;
  overallYtdValue: number;
  powerPlantAverages: EnergyPerformanceIndexPowerPlantAverage[];
  clusterAverages: EnergyPerformanceIndexClusterAverage[];
};

export type GeneratedEnergyPowerPlantTotal = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodTotal: number;
  monthlyTotals: MonthlyMetric;
  ytdTotal: number;
};

export type GeneratedEnergyClusterTotal = {
  clusterId?: string;
  clusterName: string;
  periodTotal: number;
  monthlyTotals: MonthlyMetric;
  ytdTotal: number;
};

export type RopCard1106GeneratedEnergyTaticoAggregated = {
  type: 1106;
  title: string;
  overallTotal: number;
  overallMonthlyTotals: MonthlyMetric;
  overallYtdTotal: number;
  powerPlantTotals: GeneratedEnergyPowerPlantTotal[];
  clusterTotals: GeneratedEnergyClusterTotal[];
};

export type GenerationEffectivenessPowerPlantAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodValue: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type GenerationEffectivenessClusterAverage = {
  clusterId?: string;
  clusterName: string;
  periodValue: number;
  monthlyValues: MonthlyMetric;
  ytdValue: number;
};

export type RopCard1107GenerationEffectivenessTaticoAggregated = {
  type: 1107;
  title: string;
  overallAverage: number;
  overallMonthlyValues: MonthlyMetric;
  overallYtdValue: number;
  powerPlantAverages: GenerationEffectivenessPowerPlantAverage[];
  clusterAverages: GenerationEffectivenessClusterAverage[];
};

export type PowerPlantManageableLossTotal = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodTotal: number;
  monthlyTotals: MonthlyMetric;
  ytdTotal: number;
  powerPlantsCount: number;
};

export type ClusterManageableLossTotal = {
  clusterId?: string;
  clusterName: string;
  periodTotal: number;
  monthlyTotals: MonthlyMetric;
  ytdTotal: number;
  powerPlantsCount: number;
};

export type RopCard1108ManageableLossTaticoAggregated = {
  type: 1108;
  title: string;
  overallTotal: number;
  criticalDeviationsOverallTotal: number;
  overallMonthlyTotals: MonthlyMetric;
  overallYtdTotal: number;
  powerPlantTotals: PowerPlantManageableLossTotal[];
  clusterTotals: ClusterManageableLossTotal[];
};

export type PowerPlantPerformance = {
  usinaId: number;
  usinaName: string;
  generationType: string;
  irradiance: number;
  generatedEnergy: number;
  expectedEnergy: number;
  yarlyExpectedEnergy: number;
  deviationPercent: number;
  performanceRatio: number;
  expectedPerformanceRatio: number;
  yarlyExpectedPerformanceRatio: number;
  availability: number;
};

export type ClusterPerformance = {
  clusterId?: string;
  clusterName: string;
  irradiance: number;
  generatedEnergy: number;
  expectedEnergy: number;
  yarlyExpectedEnergy: number;
  deviationPercent: number;
  performanceRatio: number;
  expectedPerformanceRatio: number;
  yarlyExpectedPerformanceRatio: number;
  availability: number;
  powerPlantsCount: number;
  powerPlants: PowerPlantPerformance[];
};

export type RopCard1109PerformanceByAssetTaticoAggregated = {
  type: 1109;
  title: string;
  clusterPerformances: ClusterPerformance[];
};

export type AvailabilityAnalysisData = {
  systemAvailability: number;
  systemExpectedValue: number;
  inverterAvailability: number;
  inverterExpectedValue: number;
  contractualAvailability: number;
  contractualExpectedValue: number;
  averageInverterUnavailabilityTime: number;
  averageTrackerUnavailabilityTime: number;
  averageStringUnavailabilityTime: number;
  disponibilidadeOnOffEnabled?: boolean;
};

export type AvailabilityAnalysisPowerPlantAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  data: AvailabilityAnalysisData;
};

export type AvailabilityAnalysisClusterAverage = {
  clusterId?: string;
  clusterName?: string;
  usinasCount?: number;
  data: AvailabilityAnalysisData;
  powerPlantAverages: AvailabilityAnalysisPowerPlantAverage[];
};

export type RopCard1111AvailabilityAnalysisTaticoAggregated = {
  type: 1111;
  title: string;
  overallAverage: AvailabilityAnalysisData;
  clusterAverages: AvailabilityAnalysisClusterAverage[];
};

export type PVSystExpectedLosses = {
  opticalLossPercentage: number;
  conversionLossPercentage: number;
  inverterLossPercentage: number;
  transformerLossPercentage: number;
};

export type RootCauseAnalysisData = {
  expectedLossesTotal: number;
  opticalAndIrradiationLosses: number;
  conversionAndDCModuleLosses: number;
  inverterEfficiencyLosses: number;
  transformerEfficiencyLosses: number;
  recoverableLossesTotal: number;
  inverterUnavailabilityLoss: number;
  stringUnavailabilityLoss: number;
  otherOperationalLosses: number;
  idealEnergy: number;
  generatedEnergy: number;
  theoreticalPredictedEnergy: number;
  predictedEnergy: number;
  theoreticalEnergyGap: number;
  predictedEnergyGap: number;
  energyBalance: number;
  pvsystReference?: PVSystExpectedLosses | null;
};

export type RootCauseAnalysisPowerPlantTotal = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  data: RootCauseAnalysisData;
};

export type RootCauseAnalysisClusterTotal = {
  clusterId?: string;
  clusterName: string;
  data: RootCauseAnalysisData;
};

export type RootCauseAnalysisPowerPlantAlarmsTotal = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  data: AlarmCategoryData;
};

export type RootCauseAnalysisClusterAlarmsTotal = {
  clusterId?: string;
  clusterName: string;
  data: AlarmCategoryData;
};

export type PriorityInsight = {
  lossCategory: string;
  lossValue: number;
  lossPercentage: number;
  isAvoidable: boolean;
  recommendation: string;
  avoidablePercentage: number;
  potentialRecovery: number;
};

export type RopCard1110RootCauseAnalysisTaticoAggregated = {
  type: 1110;
  title: string;
  overallTotal: RootCauseAnalysisData;
  overallAlarmsTotal: AlarmCategoryData;
  powerPlantTotals: RootCauseAnalysisPowerPlantTotal[];
  clusterTotals: RootCauseAnalysisClusterTotal[];
  powerPlantAlarmsTotals: RootCauseAnalysisPowerPlantAlarmsTotal[];
  clusterAlarmsTotals: RootCauseAnalysisClusterAlarmsTotal[];
  stringFailures: {
    powerPlantsFailures: RootCausePowerPlantFailures[];
    overallData: {
      count: number;
      duration: string;
    };
  };
  inverterFailures: {
    powerPlantsFailures: RootCausePowerPlantFailures[];
    overallData: {
      count: number;
      duration: string;
    };
  };
  trackerFailures: {
    powerPlantsFailures: RootCausePowerPlantFailures[];
    overallData: {
      count: number;
      duration: string;
    };
  };
  priorityInsight?: PriorityInsight | null;
};

export type RootCausePowerPlantFailures = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  count: number;
  duration: string;
  data: {
    count: number;
    duration: string;
  };
};

export type AlarmCategoryItem = {
  count: number;
  duration: string;
};

export type AlarmCategoryData = {
  trackerFailures: AlarmCategoryItem;
  stringFailures: AlarmCategoryItem;
  inverterFailures: AlarmCategoryItem;
  totalAlarms: number;
  totalDuration: string;
};

export type RecentAlarmData = {
  powerPlantName: string;
  alarmCategory: string;
  alarmDescription: string;
  severity: number;
  duration: string;
  occurrenceCount: number;
};

export type AlarmCategoryAggregatedItem = {
  totalCount: number;
  totalDuration: string;
  percentage: number;
};

export type AlarmCategoryDataAggregated = {
  trackerFailures: AlarmCategoryAggregatedItem;
  stringFailures: AlarmCategoryAggregatedItem;
  inverterFailures: AlarmCategoryAggregatedItem;
  totalAlarms: number;
  totalDuration: string;
  numberOfPowerPlants: number;
};

export type PowerPlantAlarmAverage = {
  powerPlantName: string;
  trackerFailuresCount: number;
  stringFailuresCount: number;
  inverterFailuresCount: number;
  totalAlarmsCount: number;
  totalDuration: string;
  criticalAlarmsCount: number;
};

export type ClusterAlarmAverage = {
  clusterName: string;
  averageTrackerFailures: number;
  averageStringFailures: number;
  averageInverterFailures: number;
  totalPowerPlants: number;
  totalAlarms: number;
};

export type RopCard1112CriticalAlertsAndDeviationsTaticoAggregated = {
  type: 1112;
  title: string;
  overallData: AlarmCategoryDataAggregated;
  powerPlantAverages: PowerPlantAlarmAverage[];
  topCriticalAlarms: RecentAlarmData[];
};

export type AveragePowerPowerPlantAverage = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodAverage: number;
  monthlyAverage: MonthlyMetric;
  ytdAverage: number;
};

export type AveragePowerClusterAverage = {
  clusterId?: string;
  clusterName: string;
  periodAverage: number;
  monthlyAverage: MonthlyMetric;
  ytdAverage: number;
};

export type RopCard1113AveragePowerTaticoAggregated = {
  type: 1113;
  title: string;
  overallAverage: number;
  overallMonthlyAverages: MonthlyMetric;
  overallYtdAverages: number;
  powerPlantAverages: AveragePowerPowerPlantAverage[];
  clusterAverages: AveragePowerClusterAverage[];
};

export type RopCard1114EquipmentsUnavailabilityTaticoAggregated = {
  type: 1114;
  title: string;
  clusters: ClusterUnavailabilityData[];
};

export type AlarmsConsolidatedByGroup = {
  groupName: string;
  occurrenceCount: number;
  totalDuration: string;
};

export type ClusterUnavailabilityData = {
  clusterId?: string;
  clusterName?: string;
  eventCount: number;
  criticalEventCount: number;
  stringsUnavailability: AlarmsConsolidatedByGroup;
  trackersUnavailability: AlarmsConsolidatedByGroup;
  inversorsUnavailability: AlarmsConsolidatedByGroup;
  usinas: UsinaUnavailabilityData[];
};

export type UsinaUnavailabilityData = {
  usinaId: number;
  usinaName: string;
  eventCount: number;
  criticalEventCount: number;
  stringsUnavailability: AlarmsConsolidatedByGroup;
  trackersUnavailability: AlarmsConsolidatedByGroup;
  inversorsUnavailability: AlarmsConsolidatedByGroup;
};

export type EquipmentType = "inverters" | "trackers" | "strings";

export type PlantDowntimeItem = {
  plantId: string;
  plantName: string;
  downtimeSeconds: number;
  formattedDowntime: string;
};

export type EquipmentDowntimeGroup = {
  equipmentType: EquipmentType;
  label: string;
  color: string;
  averageDowntimeSeconds: number;
  averageDowntimeFormatted: string;
  data: PlantDowntimeItem[];
};

export type CenterTacticalData = {
  availabilityCard: RopCard1101DisponibilidadeMediaTaticaoAggregated;
  productivityCard: RopCard1102ProdutividadeEspecificaTaticaoAggregated;
  targetImpactCard: RopCard1103ImpactoNaMetaTaticoAggregated;
  performanceRatioCard: RopCard1104PerformanceRatioTaticoAggregated;
  energyPerformanceIndexCard: RopCard1105EnergyPerformanceIndexTaticoAggregated;
  generatedEnergyCard: RopCard1106GeneratedEnergyTaticoAggregated;
  generationEffectivenessCard: RopCard1107GenerationEffectivenessTaticoAggregated;
  manageableLossCard: RopCard1108ManageableLossTaticoAggregated;
  performanceByAssetCard: RopCard1109PerformanceByAssetTaticoAggregated;
  rootCauseAnalysisCard: RopCard1110RootCauseAnalysisTaticoAggregated;
  availabilityAnalysisCard: RopCard1111AvailabilityAnalysisTaticoAggregated;
  criticalAlertsAndDeviationsCard: RopCard1112CriticalAlertsAndDeviationsTaticoAggregated;
  averagePowerCard: RopCard1113AveragePowerTaticoAggregated;
  equipmentsUnavailabilityCard: RopCard1114EquipmentsUnavailabilityTaticoAggregated;
};

export enum GenerationType {
  Solar = "Solar",
  Hidrica = "Hídrica",
}

export enum TacticalInterval {
  Daily = "Daily",
  Weekly = "Weekly",
  Monthly = "Monthly",
  Custom = "Custom",
}

export type EffectivenessData = {
  generatedEnergyMWh: number;
  targetEnergyMWh: number;
  effectivenessPercentage: number;
  effectivenessTarget: number;
};

export type EffectivenessPowerPlantData = {
  usinaId: number;
  usinaName: string;
  clusterId?: string;
  clusterName?: string;
  periodData: EffectivenessData;
  monthlyData: Record<string, EffectivenessData>;
  ytdData: EffectivenessData;
};

export type EffectivenessClusterData = {
  clusterId?: string | null;
  clusterName: string;
  periodData: EffectivenessData;
  monthlyData: Record<string, EffectivenessData>;
  ytdData: EffectivenessData;
  powerPlantData: EffectivenessPowerPlantData[];
};

export type RopCard1115EffectivenessAnalysisStrategicAggregated = {
  type: 1115;
  title: string;
  periodData: EffectivenessData;
  monthlyData: Record<string, EffectivenessData>;
  ytdData: EffectivenessData;
  clusterData: EffectivenessClusterData[];
};

export type CenterStrategicData = {
  averagePowerCard: RopCard1113AveragePowerTaticoAggregated;
  performanceRatioCard: RopCard1104PerformanceRatioTaticoAggregated;
  generatedEnergyCard: RopCard1106GeneratedEnergyTaticoAggregated;
  availabilityCard: RopCard1101DisponibilidadeMediaTaticaoAggregated;
  generationEffectivenessCard: RopCard1107GenerationEffectivenessTaticoAggregated;
  manageableLossCard: RopCard1108ManageableLossTaticoAggregated;
  rootCauseAnalysisCard: RopCard1110RootCauseAnalysisTaticoAggregated;
  effectivenessAnalysisCard: RopCard1115EffectivenessAnalysisStrategicAggregated;
};
